import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

import {
  Container,
  Card,
  Button,
  Form,
  Row,
  Col,
  Modal,
  Spinner,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { STUDENT_FINAL_ASSIGNMENT_API } from "../../../api/api";

import { AiOutlineClockCircle } from "react-icons/ai";
import { IoArrowBack } from "react-icons/io5";
import AssignmentThank from "../../../assets/student/assignment-thank-img.png";

import { handleApiError } from "../../../utils/apiErrorHandler";

import "./StudentFinalAssignment.css";

const StudentFinalAssignment = () => {
  const navigate = useNavigate();
  const { finalAssignmentId } = useParams();

  const [finalQuestions, setFinalQuestions] = useState([]);
  const [totalDuration, setTotalDuration] = useState(0);

  const [currentPage, setCurrentPage] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [autoSubmitted, setAutoSubmitted] = useState(false);

  const [timeLeft, setTimeLeft] = useState(0);
  const timeLeftRef = useRef(timeLeft);

  const timerRef = useRef(null);
  const syncRef = useRef(null);

  const [showTimeOverModal, setShowTimeOverModal] = useState(false);
  // const [showLeaveModal, setShowLeaveModal] = useState(false);
  // const [allowLeave] = useState(false);

  //loading
  const [loadingFinalAssignmentDetail, setFinalAssignmentDetailLoading] =
    useState(true);
  const [
    loadingFinalAssignmentSubmitting,
    setFinalAssignmentSubmittingLoading,
  ] = useState(false);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth", // optional
    });
  }, [currentPage]);

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      setFinalAssignmentDetailLoading(false);
      navigate("/signin");

      return;
    }

    const fetchFinalAssignmentDetails = async () => {
      try {
        const response = await axios.post(
          STUDENT_FINAL_ASSIGNMENT_API.GET_FINAL_QUESTIONS,
          { final_assignment_id: finalAssignmentId },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const data = response.data;

        if (data.statusCode === 200) {
          const assignmentInfo = data.assignment[0];

          const formattedQuestions = data.data.map((q, idx) => ({
            finalAssignmentQuestionId: q.final_assignment_question_id,
            question: q.question,
            options: [
              { key: "a", value: q.a },
              { key: "b", value: q.b },
              { key: "c", value: q.c },
              { key: "d", value: q.d },
            ],
            index: idx + 1,
          }));

          setFinalQuestions(formattedQuestions);

          const minutes = parseInt(assignmentInfo.timmer);
          setTotalDuration(minutes);

          // Set timer using remaining_time (for page refresh)
          if (assignmentInfo.remaining_time) {
            const [m, s] = assignmentInfo.remaining_time.split(":").map(Number);
            setTimeLeft(m * 60 + s);
          } else {
            // fallback to full timer
            setTimeLeft(minutes * 60);
          }
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error(error);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        if (error.response?.status === 404) {
          setTimeout(() => {
            navigate("/student/assignments");
          }, 1000);
          return; // ← handle 404 separately
        }
        handleApiError(error, navigate, showErrorToast, "/signin");
      } finally {
        setFinalAssignmentDetailLoading(false);
      }
    };

    fetchFinalAssignmentDetails();
  }, [finalAssignmentId, navigate]);

  // Pagination
  const questionsPerPage = 10;
  const totalQuestions = finalQuestions.length;
  // const questionsPerPage = totalQuestions <= 5 ? totalQuestions : 5;
  const totalPages = Math.ceil(totalQuestions / questionsPerPage);

  const startIndex = currentPage * questionsPerPage;
  const currentQuestions = finalQuestions.slice(
    startIndex,
    startIndex + questionsPerPage,
  );

  useEffect(() => {
    setCurrentPage(0);
  }, [finalQuestions]);

  const handleOptionChange = (qid, option) => {
    setAnswers((prev) => ({ ...prev, [qid]: option }));
  };

  const handleSubmitAssignment = useCallback(
    async (isTimeOver = false) => {
      if (loadingFinalAssignmentSubmitting || submitted) return;

      // validation
      if (!isTimeOver) {
        const unanswered = finalQuestions.filter(
          (q) => !answers[q.finalAssignmentQuestionId],
        );
        if (unanswered.length > 0) {
          // showErrorToast("Please answer all questions before submitting!");
          showErrorToast(`Please answer Question ${unanswered[0].index}`);

          // Optionally: jump to the page containing that question
          const questionIndex = unanswered[0].index - 1; // zero-based
          const page = Math.floor(questionIndex / questionsPerPage);
          setCurrentPage(page);
          return;
        }
      }

      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/signin");

        return;
      }

      const answersArray = finalQuestions.map((q) => ({
        final_assignment_question_id: q.finalAssignmentQuestionId,
        selected_answer: answers[q.finalAssignmentQuestionId] || "",
      }));

      setFinalAssignmentSubmittingLoading(true);

      try {
        const response = await axios.post(
          STUDENT_FINAL_ASSIGNMENT_API.WRITE_FINAL_EXAM,
          {
            final_assignment_id: finalAssignmentId,
            answers: answersArray,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const data = response.data;

        if (data.statusCode === 200) {
          clearInterval(timerRef.current);
          clearInterval(syncRef.current);

          if (isTimeOver) {
            setAutoSubmitted(true);
            setShowTimeOverModal(true);
          } else {
            setSubmitted(true);
          }
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (err) {
        console.error("Error Submit assignments:", err);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        if (err.response?.status === 404) {
          setTimeout(() => {
            navigate("/student/assignments");
          }, 1000);
          return; // ← handle 404 separately
        }
        handleApiError(err, navigate, showErrorToast, "/signin");
      } finally {
        setFinalAssignmentSubmittingLoading(false);
      }
    },
    [
      answers,
      finalQuestions,
      finalAssignmentId,
      loadingFinalAssignmentSubmitting,
      navigate,
      submitted,
    ],
  );

  useEffect(() => {
    timeLeftRef.current = timeLeft;
  }, [timeLeft]);

  // Timer effect + auto-submit + sync remaining time every 10s
  useEffect(() => {
    if (loadingFinalAssignmentDetail || submitted || autoSubmitted) return;

    const token = localStorage.getItem("token");
    if (!token) return;

    // Countdown every 1 second
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          clearInterval(syncRef.current);
          if (!autoSubmitted) {
            handleSubmitAssignment(true);
          }
          setShowTimeOverModal(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Sync remaining time every 10 seconds
    syncRef.current = setInterval(() => {
      const remainingTime = timeLeftRef.current;
      if (remainingTime > 0) {
        const minutes = Math.floor(remainingTime / 60);
        const seconds = remainingTime % 60;

        const token = localStorage.getItem("token");
        if (!token) return;

        axios
          .post(
            STUDENT_FINAL_ASSIGNMENT_API.UPDATED_TIME,
            {
              final_assignment_id: finalAssignmentId,
              remaining_time: `${String(minutes).padStart(2, "0")}:${String(
                seconds,
              ).padStart(2, "0")}`,
            },
            { headers: { Authorization: `Bearer ${token}` } },
          )
          .catch((err) => console.error("Update time error:", err));
      }
    }, 10000); // every 10 seconds

    return () => {
      clearInterval(timerRef.current);
      clearInterval(syncRef.current);
    };
  }, [
    submitted,
    autoSubmitted,
    loadingFinalAssignmentDetail,
    handleSubmitAssignment,
    finalAssignmentId,
  ]);

  // Format seconds → HH:MM:SS
  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  if (submitted) {
    return (
      <Container className="student-finalassignment-details-thankyou">
        <div className="student-finalassignment-details-success-card text-center">
          <img
            src={AssignmentThank}
            alt="Success"
            className="student-finalassignment-success-icon"
          />

          <h2 className="student-finalassignment-success-title">
            Congratulations!
          </h2>

          <p className="student-finalassignment-success-text">
            You've successfully completed the assignment.
            <br />
            Great job applying what you've learned this step brings you closer
            to mastering the skill.
          </p>

          <Button
            className="student-finalassignment-success-btn"
            onClick={() => navigate("/student/assignments")}
          >
            View Results
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="student-finalassignment-wrapper">
      <div className="student-final-assignment-sticky-header">
        <div className="student-dashboard-backmoving-button-wrapper">
          <button
            className="student-dashboard-backmoving-button-main"
            onClick={() => navigate(-1)}
          >
            <IoArrowBack className="student-dashboard-backmoving-button-icon" />
            <span className="student-dashboard-backmoving-button-text">
              Back
            </span>
          </button>
        </div>

        <div className="align-items-center mb-3">
          <h2 className="student-certificate-section-title">My Assignment</h2>
          <p className="student-certificate-section-description">
            View, download, and share certificates earned from your completed
            courses.
          </p>
        </div>

        <div className="student-finalassignment-header mb-4">
          <Row className="align-items-center">
            {/* LEFT CONTENT */}
            <Col md={8} sm={12}>
              <h4 className="student-finalassignment-header-title">
                Grand Assignment
              </h4>

              <div className="student-finalassignment-header-meta">
                <span className="student-finalassignment-header-meta-spannn">
                  {totalQuestions} Questions
                </span>

                <span className="dot">•</span>

                <span className="student-finalassignment-header-meta-spannn">
                  {totalDuration} Minutes
                </span>
              </div>
            </Col>

            {/* RIGHT CONTENT */}
            <Col md={4} sm={12} className="text-md-end mt-3 mt-md-0">
              <p className="student-finalassignment-header-remaining">
                <AiOutlineClockCircle className="student-finalassignment-clock-icon" />
                {formatTime(timeLeft)} Remaining
              </p>
            </Col>
          </Row>
        </div>
      </div>

      <div className="student-finalassignment-content">
        {/* Spinner Inside JSX */}
        {loadingFinalAssignmentDetail ? (
          <div className="text-center mt-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <>
            <Card className="student-finalassignment-details-card">
              {currentQuestions.map((q) => (
                <div
                  key={q.finalAssignmentQuestionId}
                  className="student-finalassignment-details-question-box"
                >
                  <div className="student-finalassignment-details-question-title">
                    <h4 className="student-finalassignment-details-questionsss">
                      {q.index}. {q.question}
                    </h4>
                    <span className="student-finalassignment-details-question-marks">
                      1 Mark
                    </span>
                  </div>

                  <Form className="student-finalassignment-form-container">
                    {q.options.map((opt) => {
                      const inputId = `q-${q.finalAssignmentQuestionId}-${opt.key}`;
                      return (
                        <Form.Check
                          key={opt.key}
                          type="radio"
                          id={inputId}
                          name={`question-${q.finalAssignmentQuestionId}`}
                          label={opt.value}
                          checked={
                            answers[q.finalAssignmentQuestionId] === opt.key
                          }
                          onChange={() =>
                            handleOptionChange(
                              q.finalAssignmentQuestionId,
                              opt.key,
                            )
                          }
                          className="student-finalassignment-details-option"
                        />
                      );
                    })}
                  </Form>
                </div>
              ))}

              <div className="student-finalassignment-details-buttons border-top">
                <div className="student-finalassignment-details-buttons-left">
                  {currentPage > 0 && (
                    <Button
                      className="student-finalassignment-details-back-btn"
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      Back
                    </Button>
                  )}
                </div>

                <div className="student-finalassignment-details-buttons-right">
                  {currentPage < totalPages - 1 && (
                    <Button
                      className="student-finalassignment-details-next-btn"
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Next
                    </Button>
                  )}

                  {currentPage === totalPages - 1 && (
                    <Button
                      className="student-finalassignment-details-submit-btn"
                      onClick={() => handleSubmitAssignment(false)}
                      disabled={loadingFinalAssignmentSubmitting}
                    >
                      {loadingFinalAssignmentSubmitting
                        ? "Submitting..."
                        : "Submit"}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          </>
        )}
      </div>

      <Modal
        show={showTimeOverModal}
        backdrop="static"
        keyboard={false}
        centered
        dialogClassName="student-finalassignment-timeover-modal"
      >
        <Modal.Body className="student-finalassignment-timeover-body text-center">
          <h4 className="student-finalassignment-timeover-title">Time Over</h4>

          <p className="student-finalassignment-timeover-text">
            Your assignment time has ended.
            <br />
            Answers will be submitted automatically.
          </p>

          <Button
            className="student-finalassignment-timeover-submit-btn"
            onClick={() => {
              setShowTimeOverModal(false);
              setSubmitted(true);
            }}
          >
            Close
          </Button>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default StudentFinalAssignment;
